import type { Workout } from '../workouts/types';
import type { ScoringSex } from '../scoring/types';
import { buildMonthlySummary } from '../workouts/monthlyBests';
import { formatLoadAndReps } from '../workouts/describeLift';
import { loadMovedLb, volumeLb } from '../workouts/loadMoved';
import { CATEGORY_LABELS, CATEGORY_ORDER } from '../scoring/categories';
import { getCurrentMonthKey } from '../workouts/types';
import {
  WORKOUT_TROPHY_TARGET,
  type MonthAwards,
  type PersonalAward,
  type SocialAward,
  type TrophyMonth,
} from './types';

/** The people an award needs to name. */
export interface AwardUser {
  displayName: string;
  scoringSex?: ScoringSex;
}

/** "Aug 22" - built from the parts so it does not read a day early west of UTC. */
function shortDate(workoutDate: string): string {
  const [year, month, day] = workoutDate.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function monthOf(workout: Workout): string {
  return workout.monthKey || workout.workoutDate.slice(0, 7);
}

/** The previous month key for "2026-08" -> "2026-07". */
function previousMonth(monthKey: string): string {
  const year = Number(monthKey.slice(0, 4));
  const month = Number(monthKey.slice(5, 7));
  return month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, '0')}`;
}

/** Workouts grouped by month, then by lifter. */
function groupByMonthAndUser(workouts: Workout[]): Map<string, Map<string, Workout[]>> {
  const months = new Map<string, Map<string, Workout[]>>();
  for (const workout of workouts) {
    const monthKey = monthOf(workout);
    let byUser = months.get(monthKey);
    if (!byUser) {
      byUser = new Map();
      months.set(monthKey, byUser);
    }
    const mine = byUser.get(workout.userId);
    if (mine) mine.push(workout);
    else byUser.set(workout.userId, [workout]);
  }
  return months;
}

/**
 * Picks a winner.
 *
 * Ties are broken by the tiebreak value (lower wins - a date string, so the
 * earlier claim holds), then by name, so the same data always names the same
 * winner rather than depending on document order.
 */
function pickWinner<T>(
  candidates: { subject: T; value: number; tiebreak: string; name: string }[],
): { subject: T; value: number } | undefined {
  let best: (typeof candidates)[number] | undefined;
  for (const candidate of candidates) {
    if (candidate.value <= 0) continue;
    if (
      !best ||
      candidate.value > best.value ||
      (candidate.value === best.value &&
        (candidate.tiebreak < best.tiebreak ||
          (candidate.tiebreak === best.tiebreak && candidate.name < best.name)))
    ) {
      best = candidate;
    }
  }
  return best;
}

/** "Push 95 · Pull 90 · Hinge 88 · Squat 103" for a completed month. */
function categoryBreakdown(workouts: Workout[]): string {
  const summary = buildMonthlySummary(workouts);
  const fields = {
    push: summary.pushBest,
    row: summary.rowBest,
    hinge: summary.hingeBest,
    squat: summary.squatBest,
  } as const;
  return CATEGORY_ORDER.filter((c) => fields[c])
    .map((c) => `${CATEGORY_LABELS[c]} ${Math.round(fields[c]!.points)}`)
    .join(' · ');
}

/**
 * Works out one month's six social awards.
 *
 * @param monthWorkouts Every lift logged that month, by everyone.
 * @param prevWorkouts The previous month's, needed only for Most Improved.
 * @param users Lifter id to name and scoring sex.
 */
export function computeSocialAwards(
  monthWorkouts: Workout[],
  prevWorkouts: Workout[],
  users: Record<string, AwardUser>,
): SocialAward[] {
  const name = (userId: string) => users[userId]?.displayName ?? 'Unknown User';
  const isFemale = (userId: string) => users[userId]?.scoringSex === 'female';

  const byUser = new Map<string, Workout[]>();
  for (const workout of monthWorkouts) {
    const mine = byUser.get(workout.userId);
    if (mine) mine.push(workout);
    else byUser.set(workout.userId, [workout]);
  }

  const prevByUser = new Map<string, Workout[]>();
  for (const workout of prevWorkouts) {
    const mine = prevByUser.get(workout.userId);
    if (mine) mine.push(workout);
    else prevByUser.set(workout.userId, [workout]);
  }

  const summaries = new Map(
    [...byUser].map(([userId, workouts]) => [userId, buildMonthlySummary(workouts)]),
  );

  const awards: SocialAward[] = [];

  // ── Champion, and champion among women ────────────────────────────────────
  // A woman who tops the overall board wins both, deliberately: the second
  // award is "best woman", not "best woman who did not already win".
  const totals = (only: (userId: string) => boolean) =>
    [...summaries]
      .filter(([userId]) => only(userId))
      .map(([userId, summary]) => ({
        subject: userId,
        value: summary.totalPoints,
        tiebreak: '',
        name: name(userId),
      }));

  const champion = pickWinner(totals(() => true));
  if (champion) {
    awards.push({
      type: 'champion',
      userId: champion.subject,
      displayName: name(champion.subject),
      value: Math.round(champion.value),
      unit: 'points',
      detail: categoryBreakdown(byUser.get(champion.subject) ?? []),
    });
  }

  const championFemale = pickWinner(totals(isFemale));
  if (championFemale) {
    awards.push({
      type: 'champion_female',
      userId: championFemale.subject,
      displayName: name(championFemale.subject),
      value: Math.round(championFemale.value),
      unit: 'points',
      detail:
        champion?.subject === championFemale.subject
          ? 'Also took the overall title'
          : categoryBreakdown(byUser.get(championFemale.subject) ?? []),
    });
  }

  // ── Most improved ─────────────────────────────────────────────────────────
  // Gated on a complete month at both ends: picking up a category you skipped
  // last month is not improvement, it is arithmetic.
  const improvements = [...summaries]
    .map(([userId, summary]) => {
      const prev = prevByUser.get(userId);
      if (!prev || summary.isIncomplete) return null;
      const prevSummary = buildMonthlySummary(prev);
      if (prevSummary.isIncomplete) return null;
      return {
        subject: { userId, from: prevSummary.totalPoints, to: summary.totalPoints },
        value: summary.totalPoints - prevSummary.totalPoints,
        tiebreak: '',
        name: name(userId),
      };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  const improved = pickWinner(improvements);
  if (improved) {
    awards.push({
      type: 'most_improved',
      userId: improved.subject.userId,
      displayName: name(improved.subject.userId),
      value: Math.round(improved.value),
      unit: 'points',
      detail: `${Math.round(improved.subject.from)} → ${Math.round(improved.subject.to)} · all four both months`,
    });
  }

  // ── Heaviest lift, overall and among women ────────────────────────────────
  const heaviestOf = (only: (userId: string) => boolean) =>
    pickWinner(
      monthWorkouts
        .filter((workout) => only(workout.userId))
        .map((workout) => ({
          subject: workout,
          value: loadMovedLb(workout),
          tiebreak: workout.workoutDate,
          name: name(workout.userId),
        })),
    );

  const heaviest = heaviestOf(() => true);
  if (heaviest) {
    awards.push({
      type: 'heaviest',
      userId: heaviest.subject.userId,
      displayName: name(heaviest.subject.userId),
      value: Math.round(heaviest.value),
      unit: 'lb',
      detail: `${heaviest.subject.exerciseNameSnapshot} · ${formatLoadAndReps(heaviest.subject)} · ${shortDate(heaviest.subject.workoutDate)}`,
    });
  }

  const heaviestFemale = heaviestOf(isFemale);
  if (heaviestFemale) {
    awards.push({
      type: 'heaviest_female',
      userId: heaviestFemale.subject.userId,
      displayName: name(heaviestFemale.subject.userId),
      value: Math.round(heaviestFemale.value),
      unit: 'lb',
      detail: `${heaviestFemale.subject.exerciseNameSnapshot} · ${formatLoadAndReps(heaviestFemale.subject)} · ${shortDate(heaviestFemale.subject.workoutDate)}`,
    });
  }

  // ── Volume ────────────────────────────────────────────────────────────────
  const volumes = [...byUser].map(([userId, workouts]) => ({
    subject: { userId, workouts },
    value: workouts.reduce((sum, workout) => sum + volumeLb(workout), 0),
    tiebreak: '',
    name: name(userId),
  }));

  const volume = pickWinner(volumes);
  if (volume) {
    const days = new Set(volume.subject.workouts.map((w) => w.workoutDate)).size;
    const counted = volume.subject.workouts.filter((w) => loadMovedLb(w) > 0).length;
    awards.push({
      type: 'volume',
      userId: volume.subject.userId,
      displayName: name(volume.subject.userId),
      value: Math.round(volume.value),
      unit: 'lb',
      detail: `${counted} weighted lifts across ${days} ${days === 1 ? 'day' : 'days'}`,
    });
  }

  return awards;
}

/**
 * Every finished month's awards, newest first.
 *
 * The current month is deliberately absent: an award is a result, and the month
 * is not over.
 */
export function computeMonthlyAwards(
  workouts: Workout[],
  users: Record<string, AwardUser>,
  currentMonthKey: string = getCurrentMonthKey(),
): MonthAwards[] {
  const months = groupByMonthAndUser(workouts);
  const flatten = (monthKey: string) => [...(months.get(monthKey)?.values() ?? [])].flat();

  return [...months.keys()]
    .filter((monthKey) => monthKey < currentMonthKey)
    .sort((a, b) => b.localeCompare(a))
    .map((monthKey) => ({
      monthKey,
      social: computeSocialAwards(flatten(monthKey), flatten(previousMonth(monthKey)), users),
    }));
}

/**
 * One person's personal awards for one month.
 *
 * @param monthWorkouts That person's lifts in that month, and only theirs.
 */
export function computePersonalAwards(monthWorkouts: Workout[]): PersonalAward[] {
  const inOrder = [...monthWorkouts].sort((a, b) => a.workoutDate.localeCompare(b.workoutDate));

  // Participation: the first lift of the month, whatever it was.
  const participation: PersonalAward = inOrder.length
    ? { type: 'participation', earned: true, earnedDate: inOrder[0].workoutDate }
    : { type: 'participation', earned: false, progress: { have: 0, need: 1 } };

  // Finisher: the day the fourth distinct category landed.
  const seen = new Set<string>();
  let finisherDate: string | undefined;
  for (const workout of inOrder) {
    if (!CATEGORY_ORDER.includes(workout.category)) continue;
    seen.add(workout.category);
    if (seen.size === CATEGORY_ORDER.length) {
      finisherDate = workout.workoutDate;
      break;
    }
  }
  const finisher: PersonalAward = finisherDate
    ? { type: 'finisher', earned: true, earnedDate: finisherDate }
    : {
        type: 'finisher',
        earned: false,
        progress: { have: seen.size, need: CATEGORY_ORDER.length },
      };

  // Workout trophy: distinct training days, so several lifts in one session
  // count once. The day the target is reached is the day it is earned.
  const days: string[] = [];
  for (const workout of inOrder) {
    if (!days.includes(workout.workoutDate)) days.push(workout.workoutDate);
  }
  const workoutsAward: PersonalAward =
    days.length >= WORKOUT_TROPHY_TARGET
      ? { type: 'workouts', earned: true, earnedDate: days[WORKOUT_TROPHY_TARGET - 1] }
      : {
          type: 'workouts',
          earned: false,
          progress: { have: days.length, need: WORKOUT_TROPHY_TARGET },
        };

  return [participation, finisher, workoutsAward];
}

/**
 * One person's trophy case: every month they logged anything, newest first,
 * with the social awards they won and the personal ones they earned.
 */
export function buildTrophyCase(
  workouts: Workout[],
  users: Record<string, AwardUser>,
  userId: string,
  currentMonthKey: string = getCurrentMonthKey(),
): TrophyMonth[] {
  const months = groupByMonthAndUser(workouts);
  const flatten = (monthKey: string) => [...(months.get(monthKey)?.values() ?? [])].flat();

  const mine = [...months.keys()].filter((monthKey) => months.get(monthKey)?.has(userId));

  return mine
    .sort((a, b) => b.localeCompare(a))
    .map((monthKey) => ({
      monthKey,
      isCurrentMonth: monthKey >= currentMonthKey,
      social:
        monthKey < currentMonthKey
          ? computeSocialAwards(flatten(monthKey), flatten(previousMonth(monthKey)), users).filter(
              (award) => award.userId === userId,
            )
          : [],
      personal: computePersonalAwards(months.get(monthKey)!.get(userId) ?? []),
    }));
}

/** How many trophies someone holds, for the count beside their name. */
export function countTrophies(months: TrophyMonth[]): number {
  return months.reduce(
    (total, month) =>
      total + month.social.length + month.personal.filter((award) => award.earned).length,
    0,
  );
}
